import { MouseEvent, ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import { Theme, SxProps } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import TablePagination, { TablePaginationProps } from '@mui/material/TablePagination';

import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

type Props = {
  dense?: boolean;
  onChangeDense?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps<Theme>;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * Fired when the user pages. **`page` is 0-based** — it comes straight from MUI, whereas the
   * `page` prop above is 1-based. Handlers must convert: `setPage(page + 1)`.
   *
   * Storing the value unconverted leaves the table permanently stuck on page 1, because the
   * 0-based index of page 2 (`1`) equals the 1-based index of page 1. Note that `page++` is
   * *not* a conversion — post-increment evaluates to the original value.
   */
  onPageChange: (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent> | null,
    page: number
  ) => void;
  count: number;
};

export default function TablePaginationCustom({
  dense,
  onChangeDense,
  rowsPerPageOptions = [5, 10, 25],
  sx,
  onRowsPerPageChange,
  onPageChange,
  page,
  count,
  ...other
}: Props & TablePaginationProps) {
  const { t } = useTranslate();

  // `page` is 1-based here and converted to MUI's 0-based index below. A value under 1 means the
  // caller is feeding 0-based state straight in, which pins the table to page 1 — the most common
  // way to misuse this component.
  if (process.env.NODE_ENV !== 'production' && count > 0 && page < 1) {
    console.warn(
      `TablePaginationCustom: received page=${page}, but the page prop is 1-based. ` +
        `Convert in your onPageChange handler with \`page + 1\`, or the table will stay on page 1.`
    );
  }

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        page={page - 1}
        count={count}
        {...other}
        sx={{
          borderTopColor: 'transparent',
        }}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      {onChangeDense && (
        <FormControlLabel
          label={t('common.compact')}
          control={<Switch checked={dense} onChange={onChangeDense} />}
          sx={{
            pl: 2,
            py: 1.5,
            top: 0,
            position: {
              sm: 'absolute',
            },
          }}
        />
      )}
    </Box>
  );
}
